import expertPlans from "../../../shared/expert-plans.json";

/**
 * Publishes the signed-in user's expert-services discount code to the marketing
 * site.
 *
 * The marketing site is a static build on a different origin, so it has no
 * session and cannot ask the API who you are. A cookie on the shared parent
 * domain is the one channel both apps can see — the same trick
 * `helpers/click-ids.js` uses for ad attribution, and the reason it is scoped to
 * `.testdriver.ai` only on the real domain.
 *
 * The expiry is written into the value so the marketing site can ignore a stale
 * code without a network call. It is a prefill hint, nothing more: the discount is re-resolved
 * server-side from `User.createdAt` at checkout, so editing this cookie changes
 * what you see and not what you pay.
 */
const COOKIE = "td_expert_coupon";
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * When a user's discount stops applying: their signup plus the window.
 *
 * The same arithmetic `api/api/helpers/resolve-expert-coupon.js` bills on, from
 * the same `shared/expert-plans.json`. Returns 0 for a user who cannot have a
 * live discount, so callers can treat it as "no offer".
 */
export function couponExpiresAt(user) {
  if (!user || !user.couponCode || !user.createdAt) return 0;
  return user.createdAt + expertPlans.coupon.eligibleDays * DAY_MS;
}

/**
 * How long is left, in words — '' once there is nothing to count down to.
 *
 * Mirrors `marketing/src/helpers/coupon.js`: the two apps show the same offer
 * and round the same way, so a code reading "1 day left" in the console does
 * not read "0 days left" on the marketing site. Both derive the deadline from
 * `shared/expert-plans.json`, so only the wording lives in two places.
 */
export function couponDaysLeftLabel(user) {
  const expiresAt = couponExpiresAt(user);
  const remaining = expiresAt - Date.now();
  if (remaining <= 0) return "";

  const days = Math.ceil(remaining / DAY_MS);
  return days === 1 ? "1 day left" : `${days} days left`;
}

const cookieDomain = () => {
  const host = window.location.hostname;
  return /(^|\.)testdriver\.ai$/.test(host) ? "; domain=.testdriver.ai" : "";
};

/** Write (or clear) the coupon hint for the marketing site. */
export function publishExpertCoupon(user) {
  if (typeof window === "undefined") return;

  try {
    const code = user && user.couponCode;
    const createdAt = user && user.createdAt;

    if (!code || !createdAt) {
      clearExpertCoupon();
      return;
    }

    const expiresAt = couponExpiresAt(user);

    // Past the window there is nothing to advertise, and a stale banner
    // promising a discount that checkout will refuse is worse than no banner.
    if (Date.now() >= expiresAt) {
      clearExpertCoupon();
      return;
    }

    const value = encodeURIComponent(`${code}|${expiresAt}`);
    const maxAge = Math.floor((expiresAt - Date.now()) / 1000);

    document.cookie =
      `${COOKIE}=${value}; path=/${cookieDomain()}` +
      `; max-age=${maxAge}; SameSite=Lax` +
      (window.location.protocol === "https:" ? "; Secure" : "");
  } catch (e) {
    // A banner is never worth breaking a login over.
  }
}

export function clearExpertCoupon() {
  if (typeof window === "undefined") return;
  try {
    document.cookie = `${COOKIE}=; path=/${cookieDomain()}; max-age=0; SameSite=Lax`;
  } catch (e) {
    /* ignore */
  }
}
